/**
 * KPI calculator configurations – mirrors Angular kpi-calculator.config.ts.
 * Defines formulas and dependency graphs for each calculator variant.
 */
import type { CalculatorConfig } from '../models';
import {
  KPI_CORE, KPI_E, KPI_EXT, KPI_ART,
  KPI_CORE_GROUND, KPI_E_GROUND, KPI_EXT_GROUND,
  KPI_UNKNOWN_GROUND, KPI_ORDERED_GROUND,
  KPI_SP, KPI_SP_GROUND, KPI_AI,
  Period, Quarter, Week,
} from '../constants';

// ── Generic formulas ──────────────────────────────────────────────

const genericAdditionFormula = (data: Array<number[]>): number => {
  let sum = 0;
  for (const kpiData of data) {
    if (kpiData.length > 0) {
      sum += kpiData.reduce((pv, cv) => pv + cv, 0);
    }
  }
  return sum;
};

const genericArticleIndexFormula = (data: Array<number[]>): number => {
  if (data.length < 2) return 0;
  const planData = data[0];
  const groundData = data[1];
  if (!planData?.length || !groundData?.length) return 0;
  const planSum = planData.reduce((pv, cv) => pv + cv, 0);
  const groundSum = groundData.reduce((pv, cv) => pv + cv, 0);
  if (groundSum === 0) return -1;
  return (planSum / groundSum) * 100;
};

// ── Ground KPI configs ────────────────────────────────────────────

export const groundKpiCalculatorConfig_OrderedGroundEnabled: CalculatorConfig[] = [
  { kpiKey: 'SP_G', name: 'Period sum gnd', dependentKpiKeys: [KPI_ORDERED_GROUND], level: Period, formula: genericAdditionFormula },
  { kpiKey: 'SQ_G', name: 'Fin Q sum gnd', dependentKpiKeys: [KPI_ORDERED_GROUND], level: Quarter, formula: genericAdditionFormula },
];

export const groundKpiCalculatorConfig_CoreExtendedEnabled: CalculatorConfig[] = [
  { kpiKey: 'SP_G', name: 'Period sum gnd', dependentKpiKeys: [KPI_CORE_GROUND, KPI_E_GROUND, KPI_UNKNOWN_GROUND], level: Period, formula: genericAdditionFormula },
  { kpiKey: 'SQ_G', name: 'Fin Q sum gnd', dependentKpiKeys: [KPI_CORE_GROUND, KPI_E_GROUND, KPI_UNKNOWN_GROUND], level: Quarter, formula: genericAdditionFormula },
];

export const groundKpiCalculatorConfig_CoreExtendedExcitementEnabled: CalculatorConfig[] = [
  { kpiKey: 'SP_G', name: 'Period sum gnd', dependentKpiKeys: [KPI_CORE_GROUND, KPI_E_GROUND, KPI_EXT_GROUND, KPI_UNKNOWN_GROUND], level: Period, formula: genericAdditionFormula },
  { kpiKey: 'SQ_G', name: 'Fin Q sum gnd', dependentKpiKeys: [KPI_CORE_GROUND, KPI_E_GROUND, KPI_EXT_GROUND, KPI_UNKNOWN_GROUND], level: Quarter, formula: genericAdditionFormula },
];

// ── Plan KPI configs ──────────────────────────────────────────────

const aiConfig: CalculatorConfig = {
  kpiKey: KPI_AI, name: 'Article index', dependentKpiKeys: [KPI_SP, KPI_SP_GROUND], level: Period, formula: genericArticleIndexFormula,
};

export const planKpiCalculatorConfig_PlannedArticleEnabled: CalculatorConfig[] = [
  { kpiKey: 'SW', name: 'Week sum', dependentKpiKeys: [KPI_ART], level: Week, formula: genericAdditionFormula },
  { kpiKey: 'SP', name: 'Period sum', dependentKpiKeys: [KPI_ART], level: Period, formula: genericAdditionFormula },
  { kpiKey: 'SQ', name: 'Fin Q sum', dependentKpiKeys: [KPI_ART], level: Quarter, formula: genericAdditionFormula },
  aiConfig,
];

export const planKpiCalculatorConfig_CoreExtendedPlannedArticleEnabled: CalculatorConfig[] = [
  { kpiKey: 'SW', name: 'Week sum', dependentKpiKeys: [KPI_CORE, KPI_E, KPI_ART], level: Week, formula: genericAdditionFormula },
  { kpiKey: 'SP', name: 'Period sum', dependentKpiKeys: [KPI_CORE, KPI_E, KPI_ART], level: Period, formula: genericAdditionFormula },
  { kpiKey: 'SQ', name: 'Fin Q sum', dependentKpiKeys: [KPI_CORE, KPI_E, KPI_ART], level: Quarter, formula: genericAdditionFormula },
  aiConfig,
];

export const planKpiCalculatorConfig_CoreExtendedEnabled: CalculatorConfig[] = [
  { kpiKey: 'SW', name: 'Week sum', dependentKpiKeys: [KPI_CORE, KPI_E], level: Week, formula: genericAdditionFormula },
  { kpiKey: 'SP', name: 'Period sum', dependentKpiKeys: [KPI_CORE, KPI_E], level: Period, formula: genericAdditionFormula },
  { kpiKey: 'SQ', name: 'Fin Q sum', dependentKpiKeys: [KPI_CORE, KPI_E], level: Quarter, formula: genericAdditionFormula },
  aiConfig,
];

export const planKpiCalculatorConfig_CoreExtendedExcitementEnabled: CalculatorConfig[] = [
  { kpiKey: 'SW', name: 'Week sum', dependentKpiKeys: [KPI_CORE, KPI_E, KPI_EXT], level: Week, formula: genericAdditionFormula },
  { kpiKey: 'SP', name: 'Period sum', dependentKpiKeys: [KPI_CORE, KPI_E, KPI_EXT], level: Period, formula: genericAdditionFormula },
  { kpiKey: 'SQ', name: 'Fin Q sum', dependentKpiKeys: [KPI_CORE, KPI_E, KPI_EXT], level: Quarter, formula: genericAdditionFormula },
  aiConfig,
];

export const planKpiCalculatorConfig_CoreExtendedExcitementPlannedArticlesEnabled: CalculatorConfig[] = [
  { kpiKey: 'SW', name: 'Week sum', dependentKpiKeys: [KPI_CORE, KPI_E, KPI_EXT, KPI_ART], level: Week, formula: genericAdditionFormula },
  { kpiKey: 'SP', name: 'Period sum', dependentKpiKeys: [KPI_CORE, KPI_E, KPI_EXT, KPI_ART], level: Period, formula: genericAdditionFormula },
  { kpiKey: 'SQ', name: 'Fin Q sum', dependentKpiKeys: [KPI_CORE, KPI_E, KPI_EXT, KPI_ART], level: Quarter, formula: genericAdditionFormula },
  aiConfig,
];
